import React, { useMemo } from "react";
import { useLoaderData } from "react-router-dom";
import { Stack } from "react-bootstrap";
import {
  TransactionHistory,
  TransactionStatus,
  TransactionType,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import BackButton from "../../components/Buttons/BackButton/BackButton";
import SolidPlusIcon from "../../components/icons/SolidPlusIcon";
import SolidMinusIcon from "../../components/icons/SolidMinusIcon";
import BankNoteIcon from "../../components/icons/BankNoteIcon";
import TimeCircleIcon from "../../components/icons/TimeCircleIcon";
import LockIcon from "../../components/icons/LockIcon";
import {
  copyToClipboard,
  formatCurrency,
  formatTransactionDate,
} from "../../utils/helpers";
import CopyIcon from "../../components/icons/CopyIcon";
import { ThemeVariant } from "../../utils/constants/generic-types";
import { TransactionCategory } from "../../components/wallet/types";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../utils/renderHeaders";
import { usePlayGameMetaDescription } from "../../utils/constants";
import "./TransactionDetails.scss";
import useHprFiatFormatter from "../../hooks/useHprFiatFormatter";

const transactionStatusText = {
  [TransactionStatus.SUCCESS]: "Successful",
  [TransactionStatus.PENDING]: "Pending",
  [TransactionStatus.IN_PROGRESS]: "Pending",
  [TransactionStatus.FAILED]: "Failed",
  [TransactionStatus.REJECTED]: "Failed",
};

const transactionStatusStyles: Record<TransactionStatus, React.CSSProperties> =
  {
    [TransactionStatus.SUCCESS]: {
      color: "#429463",
      backgroundColor: "#E1FEED",
    },
    [TransactionStatus.PENDING]: {
      color: "#000000",
      backgroundColor: "rgba(249, 227, 36, 0.40)",
    },
    [TransactionStatus.IN_PROGRESS]: {
      color: "#000000",
      backgroundColor: "rgba(249, 227, 36, 0.40)",
    },
    [TransactionStatus.FAILED]: {
      color: "#D82C0D",
      backgroundColor: "rgba(216, 44, 13, 0.10)",
    },
    [TransactionStatus.REJECTED]: {
      color: "#D82C0D",
      backgroundColor: "rgba(216, 44, 13, 0.10)",
    },
  };

export default function TransactionDetails() {
  const { t } = useTranslation();
  const metaDescriptions = usePlayGameMetaDescription();
  const transaction = useLoaderData() as TransactionHistory;
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();

  const transactionCategory = useMemo(() => {
    const isCreditTransaction =
      transaction.type === TransactionType.TOPUP ||
      transaction.type === TransactionType.REWARD ||
      transaction.type === TransactionType.CRYPTO_DEPOSIT;
    if (isCreditTransaction) return TransactionCategory.CREDIT;
    return TransactionCategory.DEBIT;
  }, [transaction.type]);

  const displayAmount = useMemo(() => {
    if (isFiatDisplay) {
      const fiatValue = formatHprToFiatValue(transaction.amount);
      return (
        <Stack direction="horizontal" style={{ gap: 2 }}>
          <span className="HprText">{fiatValue.charAt(0)}</span>
          {fiatValue.slice(1)}
        </Stack>
      );
    }
    return (
      <>
        {transaction.amount} <span className="HprText">HPR</span>
      </>
    );
  }, [isFiatDisplay, transaction.amount, formatHprToFiatValue]);

  const detailAmount = useMemo(() => {
    if (isFiatDisplay) {
      return formatHprToFiatValue(transaction.amount);
    }
    return formatCurrency(transaction.amount, transaction.currency);
  }, [
    isFiatDisplay,
    transaction.amount,
    transaction.currency,
    formatHprToFiatValue,
  ]);

  return (
    <>
      {renderHeaders({
        title: t("metadata:transaction_history_hyper_gaming_app"),
        description: metaDescriptions.generic,
        ogTitle: extractOgTitleFromMainTitle(
          t("metadata:transaction_history_hyper_gaming_app")
        ),
      })}

      <div className="TransactionDetails">
        <div className="Header">
          <BackButton
            colorVariant={ThemeVariant.DARK}
            style={{ position: "static" }}
          />
          <Stack className="align-items-center">
            <div className="rounded-circle TransactionTypeIcon">
              {transactionCategory === TransactionCategory.CREDIT ? (
                <SolidPlusIcon width={34} height={34} />
              ) : (
                <SolidMinusIcon width={34} height={34} />
              )}
            </div>
            <Stack className="TitleAmountStatus">
              <p className="text-capitalize Grotesk Title">
                {transaction.type.replace("_", " ").toLowerCase()}
              </p>
              <p className="fw-bold Grotesk SpecialFontSettings Amount">
                {displayAmount}
              </p>
              {transaction.status && (
                <p
                  className="Grotesk SpecialFontSettings Status"
                  style={transactionStatusStyles[transaction.status]}
                >
                  {transactionStatusText[transaction.status]}
                </p>
              )}
            </Stack>
          </Stack>
        </div>
        <Stack gap={4} className="px-3 pt-4">
          <div className="d-flex justify-content-between align-items-center">
            <Stack direction="horizontal" className="TransactionDetailHeading">
              <div className="rounded-circle Icon">
                <BankNoteIcon />
              </div>
              <p className="Grotesk SpecialFontSettings TransactionDetailTitle">
                Amount
              </p>
            </Stack>
            <p className="Grotesk SpecialFontSettings TransactionDetailValue">
              {detailAmount}
            </p>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <Stack direction="horizontal" className="TransactionDetailHeading">
              <div className="rounded-circle Icon">
                <TimeCircleIcon />
              </div>
              <p className="Grotesk SpecialFontSettings TransactionDetailTitle">
                Date
              </p>
            </Stack>
            <p className="Grotesk SpecialFontSettings TransactionDetailValue">
              {formatTransactionDate(transaction.createdAt || "", true)}
            </p>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <Stack direction="horizontal" className="TransactionDetailHeading">
              <div className="rounded-circle Icon">
                <LockIcon />
              </div>
              <p className="Grotesk SpecialFontSettings TransactionDetailTitle">
                Transaction ID
              </p>
            </Stack>
            <Stack direction="horizontal" style={{ gap: 12 }}>
              <p className="text-decoration-underline Grotesk SpecialFontSettings TransactionDetailValue TransactionId">
                {transaction.id.slice(0, 13)}...
              </p>
              <button
                className="bg-transparent d-inline-flex px-0"
                onClick={() => copyToClipboard(transaction.id)}
              >
                <CopyIcon />
              </button>
            </Stack>
          </div>
        </Stack>
      </div>
    </>
  );
}
